import { forwardRef } from "react";

const COLORS = {
    red: "#B31F27",
    redDark: "#8C171E",
    redTint: "#FBEAEA",
    green: "#1E7B3B",
    greenTint: "#EAF6EE",
    ink: "#1F2328",
    muted: "#6B7280",
    border: "#E4E1DC",
    stripe: "#FAF8F5",
    paper: "#FFFFFF",
};

const FONT = "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const formatInt = (n) => Number(n ?? 0).toLocaleString("pt-BR");

const formatDecimal = (n, digits = 2) =>
    Number(n ?? 0).toLocaleString("pt-BR", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

const formatPercent = (n, digits = 2) => `${formatDecimal(n, digits)}%`;

const capitalize = (str = "") => str.charAt(0) + str.slice(1).toLowerCase();

function SectionHeader({ icon, title, right }) {
    return (
        <div
            style={{
                background: COLORS.red,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 14px",
                borderRadius: "8px 8px 0 0",
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: "uppercase",
            }}
        >
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {icon && <i className={`pi ${icon}`} style={{ fontSize: 13 }} />}
                {title}
            </span>
            {right && <span style={{ fontWeight: 600, textTransform: "none", opacity: 0.92 }}>{right}</span>}
        </div>
    );
}

function StatusPill({ status }) {
    const dentro = status === "Dentro";
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "2px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                background: dentro ? COLORS.greenTint : COLORS.redTint,
                color: dentro ? COLORS.green : COLORS.red,
            }}
        >
            <i className={`pi ${dentro ? "pi-check-circle" : "pi-times-circle"}`} style={{ fontSize: 12 }} />
            {status}
        </span>
    );
}

function KpiCell({ label, value, meta, eficiencia }) {
    return (
        <div style={{ background: COLORS.paper, padding: "8px 14px" }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: COLORS.muted, fontWeight: 700 }}>
                {label}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2 }}>
                <span style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{value}</span>
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: eficiencia >= 100 ? COLORS.green : COLORS.red,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {formatDecimal(eficiencia, 1)}%
                </span>
            </div>
            <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 2 }}>{meta}</div>
        </div>
    );
}

const sectionBox = {
    marginTop: 10,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    overflow: "hidden",
    background: COLORS.paper,
};

const th = {
    textAlign: "left",
    fontSize: 10.5,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: COLORS.muted,
    fontWeight: 700,
    padding: "5px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
};

const td = {
    padding: "5px 14px",
    fontSize: 13,
    color: COLORS.ink,
    borderBottom: `1px solid ${COLORS.border}`,
};

const QualidadeDiario = forwardRef(function QualidadeDiario(
    { data, foco, onFocoChange },
    ref
) {
    return (
        <div
            ref={ref}
            style={{
                width: 600,
                maxWidth: "100%",
                margin: "0 auto",
                background: COLORS.paper,
                fontFamily: FONT,
                color: COLORS.ink,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 10,
                padding: 16,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
        >
            {/* Cabeçalho */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: 10,
                    borderBottom: `2px solid ${COLORS.red}`,
                }}
            >
                <div>
                    <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.2 }}>Relatório Qualidade</div>
                    <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                        Data de referência:{" "}
                        <strong style={{ color: COLORS.ink }}>{data.resultado_do_dia?.data}</strong>
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <svg width="26" height="26" viewBox="0 0 24 24">
                        <polygon points="12,2 22,20 2,20" fill={COLORS.red} />
                    </svg>
                    <div style={{ textAlign: "left", lineHeight: 1.1 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.red }}>CAMBOS</div>
                        <div style={{ fontSize: 9.5, letterSpacing: 3, color: COLORS.muted }}>TÊXTIL</div>
                    </div>
                </div>
            </div>

            {/* Acumulado Mês */}
            <div style={sectionBox}>
                <SectionHeader
                    icon="pi-calendar"
                    title="Acumulado Mês"
                    right={`${data.acumulado_mes.data_inicial} — ${data.acumulado_mes.data_final}`}
                />
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                        background: COLORS.border,
                    }}
                >
                    <KpiCell
                        label="Produção"
                        value={formatInt(data.acumulado_mes.producao)}
                        meta={`Meta: ${formatInt(data.acumulado_mes.meta_producao)}`}
                        eficiencia={data.acumulado_mes.eficiencia}
                    />
                    <KpiCell
                        label="Média diária"
                        value={formatInt(data.acumulado_mes.media_diaria)}
                        meta={`Meta: ${formatInt(data.acumulado_mes.meta_diaria)}`}
                        eficiencia={data.acumulado_mes.eficiencia_diaria}
                    />
                </div>
            </div>

            {/* Índice de Defeito */}
            <div style={sectionBox}>
                <SectionHeader icon="pi-percentage" title="Índice de Defeito" />
                <div style={{ display: "flex" }}>
                    <div
                        style={{
                            flex: 1,
                            padding: "8px 14px",
                            borderRight: `1px solid ${COLORS.border}`,
                            background: COLORS.redTint,
                        }}
                    >
                        <div style={{ fontSize: 10.5, textTransform: "uppercase", color: COLORS.muted, fontWeight: 700 }}>
                            Atual
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.red, fontVariantNumeric: "tabular-nums" }}>
                            {formatPercent(data.indice_defeito.atual)}
                        </div>
                    </div>
                    <div style={{ flex: 1, padding: "8px 14px" }}>
                        <div style={{ fontSize: 10.5, textTransform: "uppercase", color: COLORS.muted, fontWeight: 700 }}>
                            Meta
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                            {formatPercent(data.indice_defeito.meta)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Resultado por setor mês */}
            <div style={sectionBox}>
                <SectionHeader icon="pi-chart-bar" title="Resultado por setor — mês" />
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={th}>Setor</th>
                            <th style={{ ...th, textAlign: "right" }}>Atual</th>
                            <th style={{ ...th, textAlign: "center" }}>Status</th>
                            <th style={{ ...th, textAlign: "right" }}>Meta</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.indice_defeito.setores.map((s, i) => (
                            <tr key={s.setor} style={{ background: i % 2 ? COLORS.stripe : COLORS.paper }}>
                                <td style={{ ...td, fontWeight: 600 }}>{capitalize(s.setor)}</td>
                                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                    {formatPercent(s.atual)}
                                </td>
                                <td style={{ ...td, textAlign: "center" }}>
                                    <StatusPill status={s.status} />
                                </td>
                                <td style={{ ...td, textAlign: "right", color: COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
                                    {formatPercent(s.meta)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Resultado do dia */}
            <div style={sectionBox}>
                <SectionHeader icon="pi-key" title="Resultado do dia" right={data.resultado_do_dia.data} />
                <div
                    style={{
                        padding: "8px 14px",
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                    }}
                >
                    <span style={{ fontSize: 13, color: COLORS.muted }}>Total de defeitos</span>
                    <span style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                        {formatDecimal(data.resultado_do_dia.total_defeitos)} m
                    </span>
                </div>

                <div style={{ borderTop: `1px solid ${COLORS.border}` }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                            {data.resultado_do_dia.defeito_por_setor.map((d, i) => (
                                <tr key={d.setor} style={{ background: i % 2 ? COLORS.stripe : COLORS.paper }}>
                                    <td style={td}>{capitalize(d.setor)}</td>
                                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                        {formatDecimal(d.total)} m
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Principais defeitos do dia */}
            <div style={sectionBox}>
                <SectionHeader icon="pi-exclamation-triangle" title="Principais defeitos do dia" />
                <div>
                    {data.resultado_do_dia.principais_defeitos.map((d, i) => (
                        <div
                            key={d.defeito}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "6px 14px",
                                borderBottom:
                                    i < data.resultado_do_dia.principais_defeitos.length - 1
                                        ? `1px solid ${COLORS.border}`
                                        : "none",
                                background: i % 2 ? COLORS.stripe : COLORS.paper,
                            }}
                        >
                            <span
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    background: COLORS.red,
                                    color: "#fff",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                {i + 1}
                            </span>
                            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{d.defeito}</span>
                            <span style={{ fontSize: 13.5, fontVariantNumeric: "tabular-nums" }}>
                                {formatDecimal(d.metragem)} m
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Foco do dia */}
            <div style={sectionBox}>
                <SectionHeader icon="pi-flag" title="Foco do dia" />
                <textarea
                    value={foco}
                    onChange={(e) => onFocoChange(e.target.value)}
                    placeholder="Escreva aqui o foco do dia..."
                    style={{
                        width: "100%",
                        minHeight: 40,
                        border: "none",
                        outline: "none",
                        padding: "8px 14px",
                        fontFamily: FONT,
                        fontSize: 13.5,
                        color: COLORS.ink,
                        resize: "vertical",
                        boxSizing: "border-box",
                    }}
                />
            </div>

            <div style={{ textAlign: "right", fontSize: 10.5, color: COLORS.muted, marginTop: 10 }}>
                Gerado em {new Date().toLocaleString("pt-BR")}
            </div>
        </div>
    );
});

export default QualidadeDiario;